import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  SearchIcon,
  FilterIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DownloadIcon,
} from "@heroicons/react/outline";
import { getAllPaymentsThunk } from "../../../store/thunks/jewelleryPlan/paymentThunks";
import {
  PAYMENT_STATUS,
  PAYMENT_METHODS,
} from "../../../constants/jewellery.constant";

const PaymentsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    payments = [],
    pagination = {},
    loading,
  } = useSelector((state) => state.jewelleryPlan?.payments || {});
  const { selectedBranch } = useSelector(
    (state) => state.jewelleryPlan?.jewellery
  );

  const [filters, setFilters] = useState({
    status: "",
    method: "",
    dateFrom: "",
    dateTo: "",
    searchTerm: "",
  });

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchPayments();
  }, [currentPage, filters.status, filters.method, selectedBranch]);

  const fetchPayments = () => {
    const params = {
      page: currentPage,
      limit: 20,
      branchId: selectedBranch,
      ...(filters.status && { status: filters.status }),
      ...(filters.method && { method: filters.method }),
      ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
      ...(filters.dateTo && { dateTo: filters.dateTo }),
      ...(filters.searchTerm && { search: filters.searchTerm }),
    };
    dispatch(getAllPaymentsThunk(params));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPayments();
  };

  const handleReset = () => {
    setFilters({
      status: "",
      method: "",
      dateFrom: "",
      dateTo: "",
      searchTerm: "",
    });
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case PAYMENT_STATUS.VERIFIED:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-light text-success-dark">
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            Verified
          </span>
        );
      case PAYMENT_STATUS.REJECTED:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger-light text-danger-dark">
            <XCircleIcon className="h-3 w-3 mr-1" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-light text-warning-dark">
            <ClockIcon className="h-3 w-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  const exportPayments = () => {
    console.log("Export payments");
  };

  if (loading && !payments.length) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="bg-background shadow rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-text">All Payments</h1>
            <p className="mt-1 text-sm text-neutral-dark">
              View and manage all payment records
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate("/jewellery/payments/verify")}
              className="inline-flex items-center px-4 py-2 border border-neutral-light rounded-md shadow-sm text-sm font-medium text-text bg-background hover:bg-neutral-light"
            >
              <ClockIcon className="h-4 w-4 mr-2" />
              Pending ({pagination.pendingCount || 0})
            </button>
            <button
              onClick={exportPayments}
              className="inline-flex items-center px-4 py-2 border border-neutral-light rounded-md shadow-sm text-sm font-medium text-text bg-background hover:bg-neutral-light"
            >
              <DownloadIcon className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-background shadow rounded-lg p-4 mb-6">
        <form onSubmit={handleSearch}>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon className="h-5 w-5 text-neutral-dark" />
                </div>
                <input
                  type="text"
                  value={filters.searchTerm}
                  onChange={(e) =>
                    setFilters({ ...filters, searchTerm: e.target.value })
                  }
                  placeholder="Search plan or customer..."
                  className="block w-full pl-10 pr-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
                className="block w-full border border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              >
                <option value="">All Status</option>
                {Object.values(PAYMENT_STATUS).map((status) => (
                  <option key={status} value={status}>
                    {status.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>

            {/* Method Filter */}
            <div>
              <select
                value={filters.method}
                onChange={(e) =>
                  setFilters({ ...filters, method: e.target.value })
                }
                className="block w-full border border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              >
                <option value="">All Methods</option>
                {Object.values(PAYMENT_METHODS).map((method) => (
                  <option key={method} value={method}>
                    {method.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>

            {/* Date From */}
            <div>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) =>
                  setFilters({ ...filters, dateFrom: e.target.value })
                }
                className="block w-full border border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>

            {/* Date To */}
            <div>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) =>
                  setFilters({ ...filters, dateTo: e.target.value })
                }
                className="block w-full border border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div className="mt-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 border border-neutral-light rounded-md text-sm font-medium text-text bg-background hover:bg-neutral-light"
            >
              Reset
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-text-on-primary rounded-md text-sm font-medium hover:bg-primary-dark"
            >
              Apply Filters
            </button>
          </div>
        </form>
      </div>

      {/* Payments Table */}
      <div className="bg-background shadow rounded-lg overflow-hidden">
        {payments.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-neutral-dark">No payments found</p>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-neutral-light">
              <thead className="bg-neutral-light">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                    Payment #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                    Plan / Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                    Entered By
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-dark uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-background divide-y divide-neutral-light">
                {payments.map((payment) => (
                  <tr key={payment._id} className="hover:bg-neutral-light">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text">
                      {payment.paymentNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-text">
                          {payment.planId?.planNumber}
                        </p>
                        <p className="text-xs text-neutral-dark">
                          {payment.userId?.name}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-text">
                          ₹{payment.amountCollected?.toLocaleString()}
                        </p>
                        {payment.verifiedAmount &&
                          payment.verifiedAmount !==
                            payment.amountCollected && (
                            <p className="text-xs text-neutral-dark">
                              Verified: ₹
                              {payment.verifiedAmount?.toLocaleString()}
                            </p>
                          )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                      {payment.method}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                      {format(new Date(payment.dateCollected), "dd MMM yyyy")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(payment.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-dark">
                      {payment.enteredBy?.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() =>
                          navigate(`/jewellery/payments/${payment._id}`)
                        }
                        className="text-primary hover:text-primary-dark"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentsList;
